/**
 * The case price ladder (blocks/case-ladder.liquid), on the product page and in the quick add
 * picker. Keeps the ladder and the quantity selector in step, so they can never hold two answers:
 *
 *   - The case tiers (6 and 12 as set in Theme settings > Case discounts) are the rungs. "1 bottle"
 *     follows the selector from one to one below the first tier (five, as set): its label, price
 *     and line come from the per-count data the block renders (no money or plural formatting here).
 *   - THE LADDER NEVER CAPS THE QUANTITY (GV-16, Matt 2026-10-01). Customers order 24 of one wine.
 *     The selector's ceiling is the server's alone: the stock left when Shopify will refuse more,
 *     or none. Until then "1 bottle" stopped the stepper at five and a case rung hid it, so 24 of
 *     a wine could not be put in the basket from the product page or quick add at all. The stepper
 *     now stays on screen beside every rung: stepping onto 6 or 12 ticks that rung, and any other
 *     count above five ticks none, so the two can still never hold different answers.
 *   - Choosing a rung sets the quantity through the selector's own methods, so its buttons, clamp
 *     and update event behave exactly as if the shopper had used it.
 *   - The case rungs' prices and lines follow the purchase option: the case saving is for one-off
 *     orders, so with a plan chosen they show the plan's price and say the saving is one-off.
 *   - A case rung can be chosen only while that many bottles can still be added: the stock left,
 *     less what is already in the basket. Otherwise it is disabled and says how many are left.
 *
 * NO STATE IS KEPT BETWEEN PRODUCTS. The quick add dialog morphs one product's picker into the
 * next, which keeps this element (and anything cached on it) alive across products. Everything is
 * therefore read from the DOM on every pass: the server's ceiling from `data-max`, the strings from
 * the JSON block, the basket from the selector's `data-cart-quantity`. When the morph changes this
 * element's attributes (another product, another variant) the ladder re-applies itself. Caching the
 * selector's ceiling once is what let a previous product's stock of 2 cap the next product's
 * "6 bottles" at 2 (REGRESSIONS.md, 2026-10-01).
 *
 * Other controls ask for a quantity with a `case-ladder:set-quantity` event on the section (the
 * upsell line's "Make it 12"), so this stays the one place that knows the rules above.
 */
import { StandardEvents } from '@shopify/events';

const QUANTITY_EVENT = 'quantity-selector:update';
const PURCHASE_EVENT = 'purchase-options:change';
export const SET_QUANTITY_EVENT = 'case-ladder:set-quantity';
/** One below the first case tier; the block renders it as data-step-cap. */
const DEFAULT_STEP_CAP = 5;

/** @typedef {{label?: string, price: string, unit: string}} RungText */
/** @typedef {{modes: Record<string, Record<string, RungText>>, left: Record<string, string>}} LadderData */

class CaseLadder extends HTMLElement {
  static observedAttributes = ['data-variant-id', 'data-max', 'data-stock', 'data-step-cap'];

  /** @type {ResizeObserver | undefined} */
  #resize;

  #queued = false;

  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.addEventListener('change', this.#onRungChange);
    this.section?.addEventListener(QUANTITY_EVENT, this.#onQuantity);
    this.section?.addEventListener(PURCHASE_EVENT, this.#onPurchase);
    this.section?.addEventListener(SET_QUANTITY_EVENT, this.#onSetQuantity);
    this.section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart);
    this.#apply(this.#quantity());
    if (typeof ResizeObserver === 'function') {
      // Only a change of width can change a wrap. Holding the units changes the height, which
      // would otherwise call this straight back (a ResizeObserver loop).
      let width = 0;
      this.#resize = new ResizeObserver(([entry]) => {
        const next = Math.round(entry?.contentRect.width ?? 0);
        if (next === width) return;
        width = next;
        this.#holdUnits();
      });
      this.#resize.observe(this);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onRungChange);
    this.section?.removeEventListener(QUANTITY_EVENT, this.#onQuantity);
    this.section?.removeEventListener(PURCHASE_EVENT, this.#onPurchase);
    this.section?.removeEventListener(SET_QUANTITY_EVENT, this.#onSetQuantity);
    this.section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCart);
    this.#resize?.disconnect();
  }

  /** Another product or variant was morphed in: re-apply once the whole morph has landed. */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected || !this.section) return;
    this.#queue();
  }

  #queue() {
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      this.#apply(this.#quantity());
    });
  }

  /** @returns {LadderData} */
  #data() {
    try {
      const data = JSON.parse(this.querySelector('script[data-case-ladder]')?.textContent || '{}');
      return { modes: data.modes || {}, left: data.left || {} };
    } catch {
      return { modes: {}, left: {} };
    }
  }

  /** The product's quantity selector: the one in this section that is not a card or a cart line. */
  #selector() {
    for (const el of this.section?.querySelectorAll('quantity-selector-component') ?? []) {
      if (!el.closest('product-card, cart-items-component')) return el;
    }
    return null;
  }

  #input() {
    return /** @type {HTMLInputElement | null} */ (this.#selector()?.querySelector('input[name="quantity"]') ?? null);
  }

  #quantity() {
    const input = this.#input();
    return input ? parseInt(input.value, 10) || 1 : 1;
  }

  /** The chosen purchase option: '' for one-off, else the plan id. */
  #plan() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.section?.querySelector('purchase-options input[type="radio"]:checked')
    );
    return checked?.value ?? '';
  }

  /** The most "1 bottle" covers: one below the first case tier (Theme settings > Case discounts). */
  get #stepCap() {
    const cap = Number(this.dataset.stepCap);
    return Number.isFinite(cap) && cap >= 1 ? cap : DEFAULT_STEP_CAP;
  }

  /** The case tiers, as the rungs carry them. */
  #tiers() {
    return [...this.querySelectorAll('input[type="radio"]')].map((radio) => Number(radio.value)).filter((qty) => qty > this.#stepCap);
  }

  /** The server's ceiling for this variant (quantity rule and stock), or Infinity for none. */
  get #max() {
    const max = this.dataset.max;
    return max ? Number(max) : Infinity;
  }

  /** What is already in the basket, as the selector knows it. */
  #inCart() {
    return parseInt(this.#input()?.getAttribute('data-cart-quantity') ?? '', 10) || 0;
  }

  /** How many more can be added: the ceiling less what is already in the basket. */
  #available() {
    return Math.max(0, this.#max - this.#inCart());
  }

  /**
   * The selector's ceiling is the server's, or none: the only ceiling there is. The selector
   * subtracts the basket from it itself (getEffectiveMax).
   */
  #release(input) {
    if (Number.isFinite(this.#max)) input.max = String(this.#max);
    else input.removeAttribute('max');
  }

  /** Sets the quantity the way the selector would. */
  #setQuantity(wanted) {
    const selector = this.#selector();
    const input = this.#input();
    if (!selector || !input || typeof selector.setValue !== 'function') return;

    this.#release(input);

    const max = selector.getEffectiveMax?.();
    const quantity = max == null ? wanted : Math.min(wanted, max);
    selector.setValue(String(quantity));
    selector.updateButtonStates?.();
    selector.onQuantityChange?.();
  }

  #onRungChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    this.#setQuantity(Number(input.value));
  };

  #onSetQuantity = (event) => {
    const quantity = Number(event.detail?.quantity);
    if (quantity > 0) this.#setQuantity(quantity);
  };

  #onQuantity = (event) => {
    if (event.target === this || !(event.target instanceof Element)) return;
    if (event.target.closest('product-card, cart-items-component')) return;
    this.#apply(event.detail?.quantity ?? this.#quantity());
  };

  #onPurchase = () => this.#apply(this.#quantity());

  /** The basket changed: product-form updates data-cart-quantity in its own handler, so read after it. */
  #onCart = () => this.#queue();

  /** Ticks the rung the quantity belongs to and relabels the rungs. Never caps or hides the selector. */
  #apply(quantity) {
    // One up to the step cap belongs to "1 bottle"; each tier is its own rung. Anything else (a
    // quantity the controls cannot produce, but a re-render could) ticks nothing and leaves the
    // selector visible, so the shopper can always see and change what is set.
    const cap = this.#stepCap;
    const rung = quantity <= cap ? 1 : this.#tiers().includes(quantity) ? quantity : 0;
    const data = this.#data();
    const plan = this.#plan();
    const texts = data.modes[plan] ?? data.modes[''] ?? Object.values(data.modes)[0] ?? {};
    const available = this.#available();

    for (const radio of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[type="radio"]'))) {
      const qty = Number(radio.value);
      const label = radio.closest('.case-ladder__rung');
      const key = qty === 1 ? String(rung === 1 ? Math.min(Math.max(quantity, 1), cap) : 1) : String(qty);
      const text = texts[key];
      const short = qty > cap && qty > available;

      radio.disabled = short;
      radio.checked = !short && qty === rung;
      label?.classList.toggle('case-ladder__rung--short', short);
      if (!label || !text) continue;
      if (text.label) label.querySelector('.case-ladder__label').textContent = text.label;
      label.querySelector('.case-ladder__price').textContent = text.price;
      label.querySelector('.case-ladder__unit').textContent = short ? data.left[String(available)] ?? '' : text.unit;
    }

    this.#holdUnits();

    const input = this.#input();
    if (input) {
      this.#release(input);
      this.#selector()?.updateButtonStates?.();
    }
  }

  /**
   * Holds each rung's line under the price at the height of the longest thing it says under any
   * purchase option (2026-10-15). On a plan a case rung reads "One-off: 5% off", which wraps on a
   * narrow rung where One-off's "5% off" does not, so every change of pill moved the Add button
   * by a line. Measured in place, so it is right at every width, and again when the width changes.
   * Only real elements are measured: the line is plain text to everything else.
   */
  #holdUnits() {
    const data = this.#data();
    const modes = Object.values(data.modes);
    if (modes.length < 2) return;
    for (const radio of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[type="radio"]'))) {
      const unit = radio.closest('.case-ladder__rung')?.querySelector('.case-ladder__unit');
      if (!(unit instanceof HTMLElement) || typeof unit.getBoundingClientRect !== 'function') continue;
      const shown = unit.textContent ?? '';
      const key = String(Number(radio.value) === 1 ? this.#stepCap : radio.value);
      unit.style.minBlockSize = '';
      let tallest = 0;
      for (const words of [shown, ...modes.map((mode) => mode[key]?.unit ?? mode['1']?.unit ?? '')]) {
        unit.textContent = words;
        tallest = Math.max(tallest, unit.getBoundingClientRect().height);
      }
      unit.textContent = shown;
      if (tallest) unit.style.minBlockSize = `${Math.ceil(tallest)}px`;
    }
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`case-ladder[data-block-id="${this.dataset.blockId}"]`);
        if (!fresh) return;
        const rungs = fresh.querySelector('[ref="rungs"]');
        const current = this.querySelector('[ref="rungs"]');
        if (rungs && current) current.replaceWith(document.importNode(rungs, true));
        const json = fresh.querySelector('script[data-case-ladder]');
        const currentJson = this.querySelector('script[data-case-ladder]');
        if (json && currentJson) currentJson.textContent = json.textContent;
        for (const key of ['stock', 'max', 'variantId', 'stepCap']) {
          if (fresh.dataset[key] === undefined) delete this.dataset[key];
          else this.dataset[key] = fresh.dataset[key];
        }
        this.#apply(this.#quantity());
      })
      .catch(() => {});
  };
}

if (!customElements.get('case-ladder')) customElements.define('case-ladder', CaseLadder);
